import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography, 
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SubscriptionIcon from '../../../../assets/main/subscription-plan.svg';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../store';
import { useSidebar } from '../../../../context/SidebarContext';
import { useBreadcrumb } from '../../../../context/BreadcrumbContext';
import { 
  fetchSubscriptionPlanDetail, 
  fetchSubscribedOrganizations,
  toggleSubscriptionPlanStatus,
  selectCurrentPlan, 
  selectCurrentPlanLoading,
  selectSubscribedOrgs,
  selectSubscribedOrgsCount,
  selectSubscribedOrgsLoading
} from '../../../../modules/subscription';
import Loader from '../../../../components/common/Loader';

import {
  PageWrapper,
  DetailCard,
  CardHeader,
  PlanInfo,
  PlanIconBox,
  StatusBadge,
  HeaderActions,
  EditButton,
  StyledTabs,
  StyledTab,
  Section,
  SectionHeader,
  SectionContent,
  DataItem,
  DataLabel,
  DataValue,
  FeatureGrid,
  FeatureBadge,
  OrgTableWrapper,
  ProgressBar,
  SubStatusBadge
} from './styles';

const SubscriptionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();

  const planData = useSelector(selectCurrentPlan);
  const loading = useSelector(selectCurrentPlanLoading);
  
  const subscribedOrgs = useSelector(selectSubscribedOrgs);
  const orgsCount = useSelector(selectSubscribedOrgsCount);
  const orgsLoading = useSelector(selectSubscribedOrgsLoading);

  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [toggling, setToggling] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleToggleClick = () => {
    setConfirmOpen(true);
  };

  const handleConfirmToggle = async () => {
    if (!planData || toggling) return;
    setConfirmOpen(false);
    setToggling(true);
    await dispatch(toggleSubscriptionPlanStatus({ planId: planData.plan_id, isActive: !planData.is_active }));
    setToggling(false);
  };

  const handleCancelToggle = () => {
    setConfirmOpen(false);
  };

  useEffect(() => {
    setActivePage('subscriptions');
    if (planData) {
      setPath([
        { title: 'Super Admin', path: '/super-admin' },
        { title: 'Subscriptions', path: '/super-admin/subscriptions' },
        { title: planData.plan_name, path: `/super-admin/subscriptions/${id}` },
      ]);
    }
    setIsShow(true);
  }, [setActivePage, setPath, setIsShow, planData, id]);

  useEffect(() => {
    if (id) {
      dispatch(fetchSubscriptionPlanDetail(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (id && activeTab === 1) {
      dispatch(fetchSubscribedOrganizations({ planId: id, page: page + 1, pageSize: rowsPerPage }));
    }
  }, [dispatch, id, activeTab, page, rowsPerPage]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    if (newValue === 1) {
      setPage(0);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (!planData && !loading) return null;

  return (
    <>
    <PageWrapper>
      {loading && <Loader open={loading} />}
      
      {planData && (
        <DetailCard>
          <CardHeader>
            <PlanInfo>
              <PlanIconBox>
                <img src={SubscriptionIcon} alt="Plan" style={{ width: '32px', height: '32px' }} />
              </PlanIconBox>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1A1C1E' }}>
                  {planData.plan_name}
                </Typography>
                <StatusBadge active={planData.is_active}>
                  {planData.is_active ? 'Active' : 'Inactive'}
                </StatusBadge>
              </Box>
            </PlanInfo>
            <HeaderActions>
              <Button
                variant="outlined"
                disabled={toggling}
                onClick={handleToggleClick}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  padding: '6px 24px',
                  borderRadius: '8px',
                  borderColor: planData.is_active ? '#d32f2f' : '#2E7D32',
                  color: planData.is_active ? '#d32f2f' : '#2E7D32',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: planData.is_active ? 'rgba(211,47,47,0.06)' : 'rgba(46,125,50,0.06)',
                    borderColor: planData.is_active ? '#d32f2f' : '#2E7D32',
                  },
                  '&:disabled': {
                    borderColor: '#ccc',
                    color: '#aaa',
                  }
                }}
              >
                {toggling ? 'Updating...' : planData.is_active ? 'Set Inactive' : 'Set Active'}
              </Button>
              <EditButton 
                variant="contained" 
                disableElevation
                onClick={() => navigate(`/super-admin/subscriptions/${id}/edit`)}
              >
                Edit Plan
              </EditButton>
            </HeaderActions>
          </CardHeader>

          <StyledTabs value={activeTab} onChange={handleTabChange}>
            <StyledTab label="Basic Details" />
            <StyledTab label="Subscribed Organizations" />
          </StyledTabs>

          <Box sx={{ p: 3 }}>
            {activeTab === 0 && (
              <>
                {/* Section 1: Plan Details */}
                <Section>
                  <SectionHeader>Plan Details</SectionHeader>
                  <SectionContent>
                    <DataItem sx={{ gridColumn: 'span 3' }}>
                      <DataLabel>Plan Name</DataLabel>
                      <DataValue>{planData.plan_name}</DataValue>
                    </DataItem>
                    <DataItem sx={{ gridColumn: 'span 3' }}>
                      <DataLabel>Description</DataLabel>
                      <DataValue>{planData.plan_description || 'N/A'}</DataValue>
                    </DataItem>
                    <DataItem sx={{ gridColumn: 'span 3' }}>
                      <DataLabel>Price</DataLabel>
                      <DataValue>${planData.plan_price_month}</DataValue>
                    </DataItem>
                    <DataItem sx={{ gridColumn: 'span 3' }}>
                      <DataLabel>Validity</DataLabel>
                      <DataValue>{planData.plan_expiry_in_days} Days</DataValue>
                    </DataItem>
                  </SectionContent>
                </Section>

                {/* Section 2: Usage Limits */}
                <Section>
                  <SectionHeader>Usage Limits</SectionHeader>
                  <SectionContent>
                    <DataItem sx={{ gridColumn: 'span 4' }}>
                      <DataLabel sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Users Allowed
                        <Tooltip title="Maximum staff users allowed">
                          <InfoOutlinedIcon sx={{ fontSize: '14px', color: '#999' }} />
                        </Tooltip>
                      </DataLabel>
                      <DataValue>{planData.number_of_staff_users}</DataValue>
                    </DataItem>
                    <DataItem sx={{ gridColumn: 'span 4' }}>
                      <DataLabel sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Workers Allowed
                        <Tooltip title="Maximum workers allowed">
                          <InfoOutlinedIcon sx={{ fontSize: '14px', color: '#999' }} />
                        </Tooltip>
                      </DataLabel>
                      <DataValue>
                        {planData.number_of_workers === -1 ? 'Unlimited' : planData.number_of_workers}
                      </DataValue>
                    </DataItem>
                    <DataItem sx={{ gridColumn: 'span 4' }}>
                      <DataLabel sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Facilities Count
                        <Tooltip title="Maximum facilities allowed">
                          <InfoOutlinedIcon sx={{ fontSize: '14px', color: '#999' }} />
                        </Tooltip>
                      </DataLabel>
                      <DataValue>
                        {planData.number_of_facilities === -1 ? 'Unlimited' : planData.number_of_facilities}
                      </DataValue>
                    </DataItem>
                  </SectionContent>
                </Section>

                {/* Section 3: Plan Features */}
                <Section>
                  <SectionHeader>Plan Features</SectionHeader>
                  <FeatureGrid>
                    {/* Handle modules_list */}
                    {planData.modules_list && planData.modules_list.map((module: any, index: number) => (
                      <FeatureBadge key={`mod-${index}`}>
                        <CheckCircleIcon />
                        {module.system_module_name || module.module_name || 'Unknown Module'}
                      </FeatureBadge>
                    ))}
                    
                    {/* Handle plan_modules fallback */}
                    {(planData as any).plan_modules && (planData as any).plan_modules.map((module: any, index: number) => (
                      <FeatureBadge key={`pmod-${index}`}>
                        <CheckCircleIcon />
                        {module.system_module_details?.module_name || 
                         module.system_module_name || 
                         module.module_name || 
                         module.name || 
                         'Unknown Module'}
                      </FeatureBadge>
                    ))}

                    {/* Handle features string fallback */}
                    {planData.features && typeof planData.features === 'string' && planData.features !== 'N/A' && 
                      planData.features.split(',').map((feature: string, index: number) => (
                        <FeatureBadge key={`feat-${index}`}>
                          <CheckCircleIcon />
                          {feature.trim()}
                        </FeatureBadge>
                      ))
                    }

                    {/* No features message */}
                    {!planData.modules_list?.length && 
                     !(planData as any).plan_modules?.length && 
                     (!planData.features || planData.features === 'N/A') && (
                      <Typography color="textSecondary" sx={{ p: 2 }}>No features assigned.</Typography>
                    )}
                  </FeatureGrid>
                </Section>
              </>
            )}

            {activeTab === 1 && (
              <OrgTableWrapper>
                {orgsLoading && <Loader open={orgsLoading} />}
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Subscription</TableCell>
                      <TableCell>Start Date</TableCell>
                      <TableCell>Expiry Date</TableCell>
                      <TableCell>User Allowed</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subscribedOrgs.map((org) => {
                      const occupancyPercent = org.total_users > 0 ? (org.current_users / org.total_users) * 100 : 0;
                      const progressColor = occupancyPercent > 80 ? '#d32f2f' : occupancyPercent > 50 ? '#FFB300' : '#5E8E7E';
                      const statusLabel = org.is_active ? 'Active' : 'Inactive';
                      
                      return (
                        <TableRow key={org.org_subscription_id}>
                          <TableCell>{String(org.org_subscription_id).padStart(3, '0')}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{org.organization_name}</TableCell>
                          <TableCell>
                            <SubStatusBadge status={statusLabel}>{statusLabel}</SubStatusBadge>
                          </TableCell>
                          <TableCell>{org.start_date ? new Date(org.start_date).toLocaleDateString() : 'N/A'}</TableCell>
                          <TableCell>{org.end_date ? new Date(org.end_date).toLocaleDateString() : 'N/A'}</TableCell>
                          <TableCell sx={{ width: '250px' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {org.current_users}/{org.total_users} Users
                              </Typography>
                            </Box>
                            <ProgressBar percentage={occupancyPercent} color={progressColor} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {subscribedOrgs.length === 0 && !orgsLoading && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                          No organizations subscribed to this plan.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={orgsCount}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </OrgTableWrapper>
            )}
          </Box>
        </DetailCard>
      )}
    </PageWrapper>

    {/* Confirmation Dialog */}
    <Dialog
      open={confirmOpen}
      onClose={handleCancelToggle}
      PaperProps={{ sx: { borderRadius: '12px', minWidth: '400px', p: 1 } }}
    >
      <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#1A1C1E' }}>
        {planData?.is_active ? 'Deactivate Plan?' : 'Activate Plan?'}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ color: '#555', fontSize: '0.95rem' }}>
          {planData?.is_active
            ? `Are you sure you want to deactivate "${planData?.plan_name}"? New organizations will not be able to subscribe. Existing subscribers will continue to have access until their subscription expiry date.`
            : `Are you sure you want to activate "${planData?.plan_name}"? It will become available for organizations to subscribe.`
          }
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={handleCancelToggle}
          sx={{
            textTransform: 'none',
            borderRadius: '8px',
            borderColor: '#ccc',
            color: '#555',
            '&:hover': { borderColor: '#999', backgroundColor: 'transparent' }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirmToggle}
          disableElevation
          sx={{
            textTransform: 'none',
            borderRadius: '8px',
            backgroundColor: planData?.is_active ? '#d32f2f' : '#2E7D32',
            '&:hover': {
              backgroundColor: planData?.is_active ? '#b71c1c' : '#1b5e20',
            }
          }}
        >
          {planData?.is_active ? 'Yes, Deactivate' : 'Yes, Activate'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default SubscriptionDetailPage;
